import React, { useEffect, useMemo, useState } from 'react';
import { getOfferComparison, errorMessage } from './offerComparisonApi';
import './OfferComparisonModal.css';

// Side-by-side comparison modal. Renders table (rows = fields, columns =
// offers) + narrative + per-offer strengths/concerns. Polls while PENDING at
// 3s so the user sees the transition without closing the modal.
const POLL_MS = 3000;

const OfferComparisonModal = ({
  transactionId,
  initial,              // comparison record from the hook
  offerLabelByOfferId,  // Map<offerId, string> — from SellerOffers, keeps column headers meaningful
  onClose,
  onRegenerate,
}) => {
  const [comparison, setComparison] = useState(initial);
  const [loadError, setLoadError] = useState(null);
  const [isRegenerating, setIsRegenerating] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const next = await getOfferComparison(transactionId);
        if (!cancelled) {
          setComparison(next);
          setLoadError(null);
        }
      } catch (err) {
        if (!cancelled) setLoadError(errorMessage(err, 'Could not refresh the comparison.'));
      }
    };
    if (comparison?.status === 'PENDING') {
      const id = setInterval(tick, POLL_MS);
      return () => { cancelled = true; clearInterval(id); };
    }
    return () => { cancelled = true; };
  }, [transactionId, comparison?.status]);

  // Ordered column list: offerId order from `comparedOfferIds` on the record
  // (service freezes this at generation time so the UI never drifts). Falls
  // back to the key set in the first table row.
  const offerIds = useMemo(() => {
    if (Array.isArray(comparison?.comparedOfferIds) && comparison.comparedOfferIds.length) {
      return comparison.comparedOfferIds;
    }
    const firstRow = comparison?.comparisonTable?.[0];
    return firstRow?.valuesByOfferId ? Object.keys(firstRow.valuesByOfferId) : [];
  }, [comparison]);

  const labelFor = (offerId) =>
    (offerLabelByOfferId && offerLabelByOfferId.get(offerId)) || `Offer ${offerId.slice(0, 8)}`;

  const handleRegenerate = async () => {
    setIsRegenerating(true);
    try {
      await onRegenerate();
    } finally {
      setIsRegenerating(false);
    }
  };

  return (
    <div className="ocm-backdrop" role="dialog" aria-modal="true" aria-labelledby="ocm-title">
      <div className="ocm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ocm-header">
          <div>
            <h3 id="ocm-title" className="ocm-title">Offer Comparison</h3>
            <p className="ocm-subtitle">
              Side-by-side across {offerIds.length} purchase-agreement {offerIds.length === 1 ? 'offer' : 'offers'}
            </p>
          </div>
          <button type="button" className="ocm-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="ocm-body">
          {loadError && (
            <p className="ocm-failed-reason">{loadError}</p>
          )}

          {comparison?.status === 'PENDING' && (
            <div className="ocm-pending">
              <div className="ocm-pending-spinner" aria-hidden="true" />
              <p>Generating a neutral comparison across all offers…</p>
            </div>
          )}

          {comparison?.status === 'FAILED' && (
            <div className="ocm-failed">
              <p className="ocm-failed-reason">
                {comparison.failureReason || 'Comparison generation failed.'}
              </p>
              <button
                type="button"
                className="ocm-retry-btn"
                onClick={handleRegenerate}
                disabled={isRegenerating}
              >
                {isRegenerating ? 'Retrying…' : 'Try again'}
              </button>
            </div>
          )}

          {comparison?.status === 'READY' && (
            <>
              {comparison.narrative && (
                <p className="ocm-narrative">{comparison.narrative}</p>
              )}

              {comparison.disclaimer && (
                <p className="ocm-disclaimer">{comparison.disclaimer}</p>
              )}

              {Array.isArray(comparison.comparisonTable) && comparison.comparisonTable.length > 0 && (
                <div className="ocm-table-wrap">
                  <table className="ocm-table">
                    <thead>
                      <tr>
                        <th className="ocm-table-field">Field</th>
                        {offerIds.map((id) => (
                          <th key={id}>{labelFor(id)}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {comparison.comparisonTable.map((row) => (
                        <tr key={row.field}>
                          <td className="ocm-table-field">{row.field}</td>
                          {offerIds.map((id) => (
                            <td key={id}>{row.valuesByOfferId?.[id] || '—'}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {Array.isArray(comparison.tradeoffs) && comparison.tradeoffs.length > 0 && (
                <div className="ocm-tradeoffs">
                  {comparison.tradeoffs.map((t) => (
                    <div className="ocm-tradeoff-card" key={t.offerId}>
                      <p className="ocm-tradeoff-label">{t.label || labelFor(t.offerId)}</p>

                      {Array.isArray(t.strengths) && t.strengths.length > 0 && (
                        <div className="ocm-tradeoff-section ocm-tradeoff-strengths">
                          <p className="ocm-tradeoff-section-title">Strengths vs. the batch</p>
                          <ul className="ocm-tradeoff-list">
                            {t.strengths.map((s, i) => <li key={i}>{s}</li>)}
                          </ul>
                        </div>
                      )}

                      {Array.isArray(t.concerns) && t.concerns.length > 0 && (
                        <div className="ocm-tradeoff-section ocm-tradeoff-concerns">
                          <p className="ocm-tradeoff-section-title">Concerns vs. the batch</p>
                          <ul className="ocm-tradeoff-list">
                            {t.concerns.map((c, i) => <li key={i}>{c}</li>)}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div className="ocm-footer">
          <span>
            {comparison?.generatedAt
              ? `Generated ${new Date(comparison.generatedAt).toLocaleString()}`
              : ''}
          </span>
          {comparison?.status === 'READY' && (
            <button
              type="button"
              className="ocm-footer-regen"
              onClick={handleRegenerate}
              disabled={isRegenerating}
            >
              {isRegenerating ? 'Regenerating…' : 'Regenerate'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OfferComparisonModal;
